// Video rendering via the browser-native WebCodecs VideoDecoder (hardware
// accelerated). Replaces RustDesk web client's heavy ogv.js WASM VP9 decoder.
//
// RustDesk streams keyframe/delta encoded chunks in VideoFrame.{vp9s,vp8s,av1s,...}.
// We wrap each EncodedVideoFrame in an EncodedVideoChunk and paint decoded frames
// to a canvas. A decoder must receive a keyframe first, so deltas are dropped
// until the first key arrives.

import { hbb } from "./proto/messages.js";

export type CodecKind = "vp9" | "vp8" | "av1" | "h264" | "h265";

// WebCodecs codec strings. VP9/VP8/AV1 need no out-of-band description.
const CODEC_STRING: Record<CodecKind, string> = {
  vp9: "vp09.00.10.08",
  vp8: "vp8",
  av1: "av01.0.04M.08",
  h264: "avc1.42E01E",
  h265: "hev1.1.6.L93.B0",
};

export class VideoRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private decoder: VideoDecoder | undefined;
  private kind: CodecKind | undefined;
  private gotKey = false;
  private onError: (e: Error) => void;

  constructor(canvas: HTMLCanvasElement, onError: (e: Error) => void = console.error) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });
    if (!ctx) throw new Error("2d canvas context unavailable");
    this.ctx = ctx;
    this.onError = onError;
  }

  static isSupported(): boolean {
    return typeof (globalThis as any).VideoDecoder !== "undefined";
  }

  private ensureDecoder(kind: CodecKind) {
    if (this.decoder && this.kind === kind && this.decoder.state !== "closed") return;
    this.reset();
    this.kind = kind;
    this.gotKey = false;
    this.decoder = new VideoDecoder({
      output: (frame) => this.paint(frame),
      error: (e) => this.onError(e instanceof Error ? e : new Error(String(e))),
    });
    this.decoder.configure({
      codec: CODEC_STRING[kind],
      optimizeForLatency: true,
    });
  }

  private paint(frame: VideoFrame) {
    try {
      const w = frame.displayWidth;
      const h = frame.displayHeight;
      if (this.canvas.width !== w || this.canvas.height !== h) {
        this.canvas.width = w;
        this.canvas.height = h;
      }
      this.ctx.drawImage(frame, 0, 0);
    } finally {
      frame.close();
    }
  }

  /** Decode one group of encoded frames. Returns after enqueueing (async decode). */
  decode(kind: CodecKind, frames: hbb.IEncodedVideoFrame[]) {
    this.ensureDecoder(kind);
    const dec = this.decoder!;
    for (const f of frames) {
      const isKey = !!f.key;
      if (!this.gotKey && !isKey) continue; // wait for first keyframe
      if (isKey) this.gotKey = true;
      const data = f.data instanceof Uint8Array ? f.data : new Uint8Array(f.data as any);
      const chunk = new EncodedVideoChunk({
        type: isKey ? "key" : "delta",
        timestamp: Number(f.pts ?? 0),
        data,
      });
      try {
        dec.decode(chunk);
      } catch (e) {
        this.onError(e instanceof Error ? e : new Error(String(e)));
        // force a fresh decoder + wait for next keyframe
        this.reset();
        return;
      }
    }
  }

  reset() {
    if (this.decoder && this.decoder.state !== "closed") {
      try {
        this.decoder.close();
      } catch {
        /* ignore */
      }
    }
    this.decoder = undefined;
    this.gotKey = false;
  }

  close() {
    this.reset();
  }
}

/** Pick the codec + frame list present in a VideoFrame protobuf. */
export function pickCodec(
  vf: hbb.IVideoFrame
): { kind: CodecKind; frames: hbb.IEncodedVideoFrame[] } | undefined {
  if (vf.vp9s?.frames?.length) return { kind: "vp9", frames: vf.vp9s.frames };
  if (vf.vp8s?.frames?.length) return { kind: "vp8", frames: vf.vp8s.frames };
  if (vf.av1s?.frames?.length) return { kind: "av1", frames: vf.av1s.frames };
  if (vf.h264s?.frames?.length) return { kind: "h264", frames: vf.h264s.frames };
  if (vf.h265s?.frames?.length) return { kind: "h265", frames: vf.h265s.frames };
  return undefined;
}
